import { useEffect, useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { ARRIVAL_REASON } from '@/features/arrival/types';
import { LANGUAGE } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';
import { useSelectArrival } from '@/shared/hooks/useSelectArrival';

import { useAssociateStatus } from '../hooks/useAssociateStatus';
import type { AssociateStatus } from '../types';
import { ASSIGNMENT_KIND } from '../types';
import { DetailList } from './DetailList';

/**
 * The card's date in the kiosk's language [ISS-144].
 *
 * `dateValue` is a calendar day (`2026-08-05`, UTC) and is formatted the way `DateCalendar` formats its days —
 * `Intl`, UTC, so the device's clock and zone can never move it to a different day. The api's `date` is en-US
 * and stays the fallback: an api that predates `dateValue`, a record with no date, or a value that is not a
 * real calendar day (malformed, out of range, or one that would roll over) all render what the api wrote.
 */
function cardDate(card: AssociateStatus, locale: string): string {
  const value = card.dateValue ?? '';
  const day = new Date(`${value}T00:00:00Z`);
  // Not a date at all (`''`, `08/05/2026`, `2026-13-01`): `Intl` would throw a RangeError and take the card down.
  if (Number.isNaN(day.getTime())) return card.date;
  // Parseable but not the day it names (`2026-02-30` rolls over to Mar 2). Also the shape check: only an exact
  // `YYYY-MM-DD` round-trips, so however an engine parses a non-ISO string, it cannot reach `Intl`.
  if (day.toISOString().slice(0, 10) !== value) return card.date;
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(day);
}

/**
 * The assignment / submission status card, and the portal deflection on it
 * [KAS-1, KAS-2; Figma · 14 · Active associate, all three viewports].
 *
 * **One card, two states.** A live placement shows the person's next shift; a submission shows their FIRST
 * DAY and frames the whole card as what they applied for. Only the date's LABEL changes — telling somebody
 * who has not been placed that they have an assignment is a promise the kiosk cannot keep.
 *
 * **No pay, rate or payday, and the guarantee is structural**: `AssociateStatus` has no such field, so there
 * is nothing here to render even by accident [KAS-1 § the narrowing IS the contract; decision 0071].
 *
 * **Reaching this card by asking creates no queue entry.** The other end of that rule lives in KID-5, which
 * does not emit for `reconfirmAssignment`; it is asserted from both ends deliberately, because a rule kept in
 * one place is a rule the other place can quietly break.
 *
 * KAS-2's deflection is the callout and the scan block: an active associate asking about hours, pay or
 * personal details is pointed at the talent portal on their own phone rather than served here, because this
 * is a shared kiosk in a public lobby and it stays read-only.
 */
export function AssociateStatusScreen() {
  const [portalHelpSent, setPortalHelpSent] = useState(false);
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const cards = useKioskSession((state) => state.associateCards);
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  const language = useKioskSession((state) => state.language);
  // The same locale choice `DateCalendar` makes, so every date on the kiosk reads in one convention.
  const locale = language === LANGUAGE.es ? 'es' : 'en-US';

  const goTo = useKioskSession((state) => state.goTo);
  const selectArrival = useSelectArrival();

  const { loading, answer } = useAssociateStatus();

  /*
   * ⭐ **No card splits in two, on what the read ANSWERED** [03-us-kas-1a; decisions 0503, 0504, 0511 Q3, 0512].
   * - `none` — an empty `200` or a `404`: the read succeeded and found nothing in the past 30 days, at any venue
   *   (KAS-1b's read). It asks whether to apply for a new job, and raises nothing.
   * - anything else — `unreachable`, or no `candidateId` so no read was made: no lookup succeeded, so it is
   *   never the empty answer. It hands off to a recruiter, and raises the entry that makes that true.
   */
  const noCard = !cards || cards.length === 0;
  const asksToApply = !loading && noCard && answer === 'none';
  const handsOff = !loading && noCard && answer !== 'none';

  /*
   * ⭐ **The hand-off raises the candidate's entry as it renders, with no tap** [0504 points 3–5; 03-us-kas-1a
   * Scenario 1]. *"A recruiter will be with you shortly"* promises a person, so an entry must stand behind it
   * [0220 L1] — a named exception to 0220 L2, for this state only [0504 point 4]. It names `needHelpOverlay`, the
   * control this screen's own *Need help?* pill sends, so the entry reads *asked for a rep* [0503 point 1; 0461].
   *
   * Screen-owned rather than `KioskScreen`'s `raisesOnArrival`, as `KioskScreen` itself says a fixed-control raise
   * should be (the pattern `FinishScreen` uses). No ref: `summonRepresentative` caps the session at one entry
   * [KOP-1 AC-14], so a StrictMode double effect, or an entry raised earlier in the visit, sends nothing more
   * (Scenario 6). Fire-and-forget [0046; BR-K7]: a failed write leaves this screen exactly as it is (Scenario 7).
   * The wait, the card and the question never reach this branch, so they never raise (Scenarios 3, 8).
   */
  useEffect(() => {
    if (handsOff) summonRepresentative(undefined, HANDOFF_CONTROL.needHelpOverlay);
  }, [handsOff, summonRepresentative]);

  /*
   * The wait replaces the screen rather than dimming it, the same way the identity lookup's does: a card
   * half-rendered with empty rows reads as an assignment with no client and no address.
   */
  if (loading) {
    return (
      <KioskScreen>
        <KioskCard header={<StatusBadge label={t('badge.kiosk')} />}>
          <div
            aria-busy="true"
            aria-live="polite"
            className="flex w-full flex-col items-center gap-4 py-12"
          >
            <Spinner />
            <p className="text-sm leading-6 text-[var(--muted-foreground)]">
              {t('associate.loading')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * ⭐ **The empty answer asks, and keeps the candidate signed in** [03-us-kas-1a Scenarios 3, 10–13; 0511 Q3
   * points 8–12; 0512 points 18–19]. No frame draws it: the existing card, heading and body, and the kit's
   * `Button`. No *Back* — *No* already goes where Back would, and *Exit* stays in the top bar (the story's Notes).
   * - *Yes* is the menu's *Search for a job* row, taken from here: the `applyForJob` selection, posted, then the
   *   openings [Scenario 11 — derived from 0511 point 9 and 0492 point 1]. Its Back then returns to the menu.
   * - *No* is the arrival menu, same session, nothing posted [Scenario 12]. A second tap on *See what I applied
   *   for* reads again and asks again, because an empty answer stores no cards (Scenario 13).
   */
  if (asksToApply) {
    return (
      <KioskScreen>
        <KioskCard
          /*
           * The affirmative first, both full width and stacked — the shape of the app's other two-way ask
           * (`PayCardAskScreen`), since no frame draws this one.
           */
          footer={
            <div className="flex w-full flex-col gap-3">
              <Button
                fullWidth
                onClick={() => {
                  selectArrival({ reason: ARRIVAL_REASON.applyForJob }, SCREEN.jobs);
                }}
              >
                {t('application.yes')}
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  goTo(SCREEN.arrivalReason);
                }}
                variant="secondary"
              >
                {t('application.no')}
              </Button>
            </div>
          }
          header={<StatusBadge label={t('badge.kiosk')} />}
        >
          <div className="flex w-full flex-col items-start gap-6 pb-8">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('associate.noneInThirtyDays')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('associate.applyForNewJob')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * **A failed read hands off to a recruiter** [03-us-kas-1a Scenarios 1, 2, 5, 16; 0504 point 1; 0510 D3].
   * No card, no invented one: an empty card would tell a candidate their assignment has no client and no
   * address. The copy is the Operator's and states no reason — this is a public lobby. The entry is raised by
   * the effect above. *Start over* ends the session and leaves the entry [0500 Q3]; *Back* is `useBack` — the
   * menu when the visit holds a selection, else the Exit question — and raises nothing. The footer and header
   * are `SeeARepScreen`'s, and *Start over* is the filled primary `FinishScreen`'s identical hand-off uses.
   *
   * Written as the no-card test rather than `handsOff` so the card branch below is narrowed; past the wait and
   * the question, no card IS the hand-off.
   */
  if (noCard) {
    return (
      <KioskScreen>
        <KioskCard
          footer={
            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
              <Button onClick={endCandidateSession}>{t('routing.assistance.startOver')}</Button>
            </div>
          }
          header={
            <div className="flex w-full items-center justify-between">
              <BackLink onClick={back} />
              <StatusBadge label={t('badge.kiosk')} />
            </div>
          }
        >
          <div className="flex w-full flex-col items-start gap-6 pb-8">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('associate.handOff.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('associate.handOff.body')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  /*
   * **One card, or a list of them** [Operator, 2026-09-08]. `03-us-kas-1` recorded multiples as unspecified
   * — *"Whether a person with two live submissions sees two cards, a list, or the most recent, was never
   * raised"* — and the ruling is a list.
   *
   * A placement is always alone: the api returns it that way (BR-K6), so `several` can only ever be
   * submissions. That is why the plural subheading talks about applications and not about assignments.
   */
  const several = cards.length > 1;
  const [first] = cards;
  const isPlacement = first!.kind === ASSIGNMENT_KIND.placement;

  return (
    <KioskScreen>
      <KioskCard
        footer={
          <Button fullWidth onClick={endCandidateSession} variant="secondary">
            {t('associate.done')}
          </Button>
        }
        header={
          <div className="flex w-full items-center justify-between">
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </div>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {/*
                The frame reads "Welcome back, Maria" and this renders the bare greeting, because the name
                is DATA the kiosk does not have: KID-2's diagnosis response carries a diagnosis, a candidate
                id and an email-on-file, and deliberately nothing else — the narrowest payload that answers
                the question was the point of that story. Adding a first name is a contract change to a
                shipped endpoint and belongs to whoever makes it, not to this screen. Reported, not invented.
              */}
              {t('associate.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {several
                ? t('associate.subheading.applications', { count: String(cards.length) })
                : isPlacement
                  ? t('associate.subheading')
                  : t('associate.subheading.applied')}
            </p>
          </div>

          {/*
            The rows are identical per card; what a list adds is a heading per application, because four
            unlabelled blocks of client/location/shift/date are unreadable stacked. A single card keeps no
            heading — it would repeat the client name that is already its first row.
          */}
          {cards.map((card, index) => (
            <div
              className="flex w-full flex-col items-start gap-2"
              key={`${card.clientName}-${index}`}
            >
              {several && (
                <h2 className="w-full text-base font-semibold leading-6 text-black">
                  {t('associate.application', { n: String(index + 1) })}
                </h2>
              )}
              <DetailList
                rows={[
                  { label: t('associate.client'), value: card.clientName },
                  // The address alone while the api sends an empty site [Operator ruling 28, 2026-09-29:
                  // "Address only" — Bullhorn holds no site name, and the order title stood in for one]. An
                  // api that still sends a site keeps "site · address"; an empty one never leaves a stray
                  // separator [ISS-144].
                  {
                    label: t('associate.location'),
                    value: [card.siteName, card.streetAddress].filter(Boolean).join(' · '),
                  },
                  { label: t('associate.shift'), value: card.shift },
                  {
                    // The one label that follows the state. Everything else on the card is identical.
                    label:
                      card.kind === ASSIGNMENT_KIND.placement
                        ? t('associate.nextShift')
                        : t('associate.firstDay'),
                    value: cardDate(card, locale),
                  },
                ]}
              />
            </div>
          ))}

          {/*
            The card NAMES the talent portal and no longer offers a control to reach it
            [decision 0114, approved 2026-08-25]. What sat here was the onward CTA — the portal's address,
            rendered as text because this repository has no QR encoder. 0114 removes the CTA itself, not
            just its QR form: "no QR code and no live redirect on any screen". KAS-1's own table now reads
            "⛔ ~~a QR to the talent portal~~ — REMOVED", and its frontend row bans rendering it.

            The deflection text stays, and that is the decision's own scope: the card still tells a placed
            associate that pay, tax and personal changes happen in the talent portal on their own phone.
            ⚠️ HOW they get there is now unspecified — 0114 § *What is NOT decided*.
          */}
          {/*
            The callout carries an ESCALATION, which the frame draws and the build did not have.
            
            KAS-2 forbids offering a control to REACH the portal, and 0114 forbids any QR or live redirect.
            This is neither: it enqueues a recruiter — "let us know and we'll notify an Integrity rep" is the
            frame's own promise — and the kiosk still holds no portal address and performs no portal
            function. An associate who cannot get into the portal had, until now, nothing to do but leave.
            
            One-way and once: the label is replaced by a confirmation rather than staying tappable, because
            a second tap would put the same person on the queue twice and a recruiter cannot tell those apart.
          */}
          <Callout
            action={
              portalHelpSent ? (
                <span className="block py-[13px] text-sm leading-5 text-[var(--primary-muted-foreground)]">
                  {t('associate.portalDeflection.done')}
                </span>
              ) : (
                <button
                  className="kiosk-focusable py-[13px] text-left text-sm leading-5 text-[var(--link)] underline"
                  onClick={() => {
                    setPortalHelpSent(true);
                    /*
                     * An explicit ask — the one shape of queue entry that is never in doubt. It goes
                     * through the session's single summon so that the candidate's OTHER promises (an I-9
                     * hand-off, the see-a-representative screen) cannot add a second row for the same
                     * person; the local `portalHelpSent` still keeps this label from being tapped twice.
                     * It names its control, so the entry reads *talent portal access* [KOP-1g scenario 2].
                     */
                    summonRepresentative(undefined, HANDOFF_CONTROL.talentPortalLink);
                  }}
                  type="button"
                >
                  {t('associate.portalDeflection.action')}
                </button>
              )
            }
          >
            <span className="block font-semibold">{t('associate.portalDeflection.title')}</span>
            <span className="mt-[10px] block">{t('associate.portalDeflection')}</span>
          </Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
